import React, { useState, useEffect } from "react";
import axios from "axios";

import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import InfoIcon from "@mui/icons-material/Info";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SaveIcon from "@mui/icons-material/Save";
import { CheckCircle } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryIcon from "@mui/icons-material/History";
import PendingIcon from "@mui/icons-material/Pending";

import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

const API_URL = process.env.REACT_APP_API_URL || "https://api.quest-connect.com/api";

// Axios interceptor to add auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function InterviewRequest() {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [activeTab, setActiveTab] = useState("new");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  const options = [
    { value: "basic_info", label: "Basic Info" },
    { value: "job", label: "Job Related Data" },
    { value: "education", label: "Educational Details" },
    { value: "salary", label: "Salary Structure" },
    { value: "documents", label: "Upload Documents" },
    { value: "account", label: "Accounts Details" },
    { value: "skills", label: "Skills" },
  ];

  const fetchMyRequests = async () => {
    setLoadingRequests(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_URL}/user/profile-update-result-notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Ensure we normalize snake_case keys to camelCase for React
      const normalizedRequests = (response.data.notifications || []).map((item) => ({
        id: item.id,
        status: item.status,
        comment: item.comment,
        sections: item.sections,
        createdAt: item.created_at,
        adminComment: item.admin_comment || "", // Handle admin_comment field
        readAt: item.read_at,
      }));

      // Sort requests by creation date (newest first)
      normalizedRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      setMyRequests(normalizedRequests);
    } catch (error) {
      console.error("Failed to fetch requests", error);
      openSnack("Failed to load your requests.", "error");
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  const openSnack = (message, color = "info") => setSnackbar({ open: true, message, color });

  const handleSubmit = async () => {
    if (selectedOptions.length === 0) {
      openSnack("Please select at least one section.", "warning");
      return;
    }
    if (!comment.trim()) {
      openSnack("Comment is required.", "warning");
      return;
    }

    setLoading(true);
    const selectedValues = selectedOptions.map((o) => o.value);
    const token = localStorage.getItem("authToken");

    try {
      await axios.post(
        `${API_URL}/user/profile-update-requests`,
        {
          sections: selectedValues,
          comment: comment.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      openSnack("Request sent successfully!", "success");
      setSelectedOptions([]);
      setComment("");
      fetchMyRequests();
      setActiveTab("history");
    } catch (error) {
      const backendMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Error sending request. Please try again.";
      openSnack(backendMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
      case "granted":
        return <CheckCircle color="success" fontSize="small" />;
      case "rejected":
        return <CancelIcon color="error" fontSize="small" />;
      case "pending":
        return <PendingIcon color="warning" fontSize="small" />;
      default:
        return <CircularProgress size={16} color="info" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Pending Review";
      case "approved":
      case "granted":
        return "Approved";
      case "rejected":
        return "Rejected";
      default:
        return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
      case "granted":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      default:
        return "info";
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "approved":
      case "granted":
        return "#4caf50";
      case "rejected":
        return "#f44336";
      case "pending":
        return "#ff9800";
      default:
        return "#2196f3";
    }
  };

  const getSectionLabel = (sectionValue) => {
    const option = options.find(opt => opt.value === sectionValue);
    return option ? option.label : sectionValue;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox mb={3}>
        <Card>
          <SoftBox display="flex" flexDirection="column" gap={3} p={3}>
            {/* Tab Navigation */}
            <SoftBox display="flex" borderBottom={1} borderColor="divider">
              <SoftButton
                variant={activeTab === "new" ? "contained" : "text"}
                color="dark"
                onClick={() => setActiveTab("new")}
                sx={{
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                  fontWeight: activeTab === "new" ? "bold" : "normal"
                }}
              >
                New Request
              </SoftButton>
              <SoftButton
                variant={activeTab === "history" ? "contained" : "text"}
                color="dark"
                onClick={() => setActiveTab("history")}
                sx={{
                  borderBottomRightRadius: 0,
                  borderBottomLeftRadius: 0,
                  fontWeight: activeTab === "history" ? "bold" : "normal"
                }}
              >
                <HistoryIcon fontSize="small" sx={{ mr: 1 }} />
                Request History ({myRequests.length})
              </SoftButton>
            </SoftBox>

            {activeTab === "new" ? (
              <>
                {/* New Request Form */}
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <ListAltIcon color="dark" />
                  <Typography variant="h6" fontWeight="bold">
                    Profile Update Request
                  </Typography>
                </SoftBox>

                <SoftBox
                  display="flex"
                  alignItems="flex-start"
                  gap={1.5}
                  bgcolor="#f0f4f8"
                  borderRadius="8px"
                  p={1.5}
                >
                  <InfoIcon color="action" />
                  <SoftTypography variant="body2" color="text.secondary">
                    Select the sections you want to update and provide a brief reason. Your request
                    will be reviewed by Admin. Once approved, you can update those sections.
                  </SoftTypography>
                </SoftBox>

                <SoftBox>
                  <SoftTypography variant="subtitle2" fontWeight="medium" mb={1}>
                    Select Sections to Update <span style={{ color: "red" }}>*</span>
                  </SoftTypography>
                  <SoftSelect
                    placeholder="Choose sections..."
                    options={options}
                    value={selectedOptions}
                    onChange={(value) => setSelectedOptions(value)}
                    isMulti
                    fullWidth
                  />
                </SoftBox>

                <SoftBox>
                  <SoftTypography variant="subtitle2" fontWeight="medium" mb={1}>
                    Comment / Reason for Request <span style={{ color: "red" }}>*</span>
                  </SoftTypography>
                  <textarea
                    className="form-control form-control-md"
                    cols={5}
                    rows={6}
                    placeholder="Explain why you need to update these sections..."
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    style={{
                      resize: "vertical",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "8px",
                      fontSize: "14px"
                    }}
                  />
                </SoftBox>

                <SoftBox display="flex" justifyContent="flex-end" gap={2}>
                  <SoftButton
                    variant="outlined"
                    color="dark"
                    onClick={() => {
                      setSelectedOptions([]);
                      setComment("");
                    }}
                    disabled={loading}
                  >
                    Clear
                  </SoftButton>
                  <SoftButton
                    variant="contained"
                    color="dark"
                    startIcon={!loading && <SaveIcon />}
                    onClick={handleSubmit}
                    disabled={loading || selectedOptions.length === 0 || !comment.trim()}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Request"}
                  </SoftButton>
                </SoftBox>
              </>
            ) : (
              <>
                {/* Request History */}
                <SoftBox display="flex" alignItems="center" gap={1}>
                  <HistoryIcon color="dark" />
                  <Typography variant="h6" fontWeight="bold">
                    My Request History
                  </Typography>
                </SoftBox>

                {loadingRequests ? (
                  <SoftBox display="flex" justifyContent="center" alignItems="center" py={3} gap={2}>
                    <CircularProgress size={24} />
                    <SoftTypography variant="body2" color="text.secondary">
                      Loading your requests...
                    </SoftTypography>
                  </SoftBox>
                ) : myRequests.length === 0 ? (
                  <SoftBox textAlign="center" py={4}>
                    <SoftTypography variant="body2" color="text.secondary">
                      No requests found. Submit a new request to get started.
                    </SoftTypography>
                  </SoftBox>
                ) : (
                  <SoftBox display="flex" flexDirection="column" gap={2}>
                    {myRequests.map((request) => (
                      <Card key={request.id} variant="outlined" sx={{ p: 2 }}>
                        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                          <SoftBox flex={1}>
                            <SoftBox display="flex" alignItems="center" gap={1} mb={1}>
                              <SoftTypography variant="h6" fontWeight="bold">
                                Request #{request.id.slice(0, 8)}...
                              </SoftTypography>
                              <SoftBox
                                sx={{
                                  backgroundColor: getStatusBadgeColor(request.status),
                                  color: "white",
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                  fontWeight: "bold"
                                }}
                              >
                                {getStatusText(request.status)}
                              </SoftBox>
                            </SoftBox>

                            <SoftTypography variant="caption" color="text.secondary">
                              Submitted: {new Date(request.createdAt).toLocaleString()}
                            </SoftTypography>
                          </SoftBox>

                          <SoftBox display="flex" alignItems="center" gap={1}>
                            {getStatusIcon(request.status)}
                          </SoftBox>
                        </SoftBox>

                        {/* Sections */}
                        <SoftBox mb={2}>
                          <SoftTypography variant="subtitle2" fontWeight="medium" mb={1}>
                            Requested Sections:
                          </SoftTypography>
                          <SoftBox display="flex" flexWrap="wrap" gap={1}>
                            {Array.isArray(request.sections) ? (
                              request.sections.map((section, index) => (
                                <SoftBox
                                  key={index}
                                  sx={{
                                    backgroundColor: "#e3f2fd",
                                    color: "#1976d2",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontWeight: "medium"
                                  }}
                                >
                                  {getSectionLabel(section)}
                                </SoftBox>
                              ))
                            ) : (
                              <SoftTypography variant="caption" color="text.secondary">
                                {request.sections}
                              </SoftTypography>
                            )}
                          </SoftBox>
                        </SoftBox>

                        {/* User Comment */}
                        {request.comment && (
                          <SoftBox mb={2}>
                            <SoftTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
                              Your Comment:
                            </SoftTypography>
                            <SoftBox
                              p={1.5}
                              bgcolor="grey.50"
                              borderRadius={1}
                              border="1px solid #e0e0e0"
                            >
                              <SoftTypography variant="body2">
                                {request.comment}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        )}

                        {/* Admin Response */}
                        {request.adminComment && (
                          <SoftBox>
                            <SoftTypography variant="subtitle2" fontWeight="medium" mb={0.5}>
                              Admins Response:
                            </SoftTypography>
                            <SoftBox
                              p={1.5}
                              bgcolor={request.status === "rejected" ? "#ffebee" : "#e8f5e8"}
                              borderRadius={1}
                              border={`1px solid ${request.status === "rejected" ? "#ffcdd2" : "#c8e6c9"}`}
                            >
                              <SoftTypography
                                variant="body2"
                                color={request.status === "rejected" ? "#d32f2f" : "#2e7d32"}
                              >
                                {request.adminComment}
                              </SoftTypography>
                            </SoftBox>
                          </SoftBox>
                        )}

                        {/* Read Status */}
                        {request.readAt && (
                          <SoftBox mt={1}>
                            <SoftTypography variant="caption" color="text.secondary">
                              Viewed: {new Date(request.readAt).toLocaleString()}
                            </SoftTypography>
                          </SoftBox>
                        )}
                      </Card>
                    ))}
                  </SoftBox>
                )}
              </>
            )}
          </SoftBox>
        </Card>
      </SoftBox>

      {/* Snackbar Notification */}
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Notification"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        time={3000}
        dateTime={new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default InterviewRequest;